/**
 * AdminUsersPage - User management for super admins
 * Lists all registered users with search, company membership count, diagnosis count.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from '../../contexts/LanguageContext';
import adminService from '../../services/adminService';
import AdminLayout from './AdminLayout';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const Content = styled.div`
  animation: ${fadeIn} 0.35s ease-out;
`;

const SearchRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  align-items: center;

  @media (max-width: 640px) { flex-direction: column; }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 10px 16px;
  font-size: 0.88rem;
  background: ${(p) => p.theme.colors.bgTertiary};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: 8px;
  color: ${(p) => p.theme.colors.text};
  font-family: ${(p) => p.theme.fonts.main};

  &:focus {
    outline: none;
    border-color: ${(p) => p.theme.colors.primary};
  }
  &::placeholder {
    color: ${(p) => p.theme.colors.textMuted};
  }
`;

const CountBadge = styled.span`
  font-size: 0.78rem;
  color: ${(p) => p.theme.colors.textSecondary};
  white-space: nowrap;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  background: ${(p) => p.theme.colors.bgSecondary};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: 12px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${(p) => p.theme.colors.textSecondary};
  border-bottom: 1px solid ${(p) => p.theme.colors.border};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 0.88rem;
  color: ${(p) => p.theme.colors.text};
  border-bottom: 1px solid ${(p) => p.theme.colors.border};
`;

const Tr = styled.tr`
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: ${(p) => p.theme.colors.bgTertiary}; }
  &:last-child td { border-bottom: none; }
`;

const AdminBadge = styled.span`
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 700;
  background: rgba(236, 72, 153, 0.15);
  color: #EC4899;
  border: 1px solid #EC4899;
  margin-left: 8px;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 20px;
`;

const PageButton = styled.button`
  padding: 6px 14px;
  font-size: 0.82rem;
  border-radius: 6px;
  border: 1px solid ${(p) => p.$active ? p.theme.colors.primary : p.theme.colors.border};
  background: ${(p) => p.$active ? p.theme.colors.primary : 'transparent'};
  color: ${(p) => p.$active ? '#fff' : p.theme.colors.textSecondary};
  cursor: pointer;
  font-family: ${(p) => p.theme.fonts.main};

  &:hover:not(:disabled) {
    border-color: ${(p) => p.theme.colors.primary};
  }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const MobileCardList = styled.div`
  display: none;
  flex-direction: column;
  gap: 8px;

  @media (max-width: ${(p) => p.theme.breakpoints.tablet}) {
    display: flex;
  }
`;

const DesktopTable = styled.div`
  @media (max-width: ${(p) => p.theme.breakpoints.tablet}) {
    display: none;
  }
`;

const MobileCard = styled.div`
  background: ${(p) => p.theme.colors.bgSecondary};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: 10px;
  padding: 14px 16px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover { background: ${(p) => p.theme.colors.bgTertiary}; }
`;

const MobileCardName = styled.div`
  font-weight: 600;
  font-size: 0.9rem;
  color: ${(p) => p.theme.colors.text};
  margin-bottom: 4px;
`;

const MobileCardEmail = styled.div`
  font-size: 0.78rem;
  color: ${(p) => p.theme.colors.textMuted};
  margin-bottom: 8px;
`;

const MobileCardStats = styled.div`
  display: flex;
  gap: 16px;
  font-size: 0.78rem;
  color: ${(p) => p.theme.colors.textSecondary};
  font-family: ${(p) => p.theme.fonts?.mono};
`;

const SkeletonBlock = styled.div`
  background: linear-gradient(90deg,
    ${(p) => p.theme.colors.bgTertiary} 25%,
    ${(p) => p.theme.colors.bgSecondary} 50%,
    ${(p) => p.theme.colors.bgTertiary} 75%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.6s infinite;
  border-radius: 8px;
  height: ${(p) => p.$h || '20px'};
  width: ${(p) => p.$w || '100%'};
  margin-bottom: ${(p) => p.$mb || '0'};
`;

function AdminUsersPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUsers = useCallback(async (p = 1, q = '') => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminService.getUsers({ page: p, per_page: 50, q: q || undefined });
      setUsers(data.users || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      setPage(data.page || 1);
    } catch (err) {
      setError(err.message || t('データの取得に失敗しました', 'Failed to fetch data'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchUsers(1, '');
  }, [fetchUsers]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      fetchUsers(1, search);
    }
  };

  if (error) {
    return (
      <AdminLayout active="users">
        <Content style={{ textAlign: 'center', padding: 64 }}>
          <p style={{ color: '#EF4444' }}>{error}</p>
        </Content>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout active="users">
      <Content>
        <SearchRow>
          <SearchInput
            placeholder={t('メール、名前で検索...', 'Search by email or name...')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <CountBadge>{total} {t('件', 'users')}</CountBadge>
        </SearchRow>

        {loading ? (
          <SkeletonBlock $h="400px" $w="100%" />
        ) : (
          <>
            <DesktopTable>
              <TableWrapper>
                <Table>
                  <thead>
                    <tr>
                      <Th>ID</Th>
                      <Th>{t('名前', 'Name')}</Th>
                      <Th>{t('メール', 'Email')}</Th>
                      <Th>{t('企業', 'Companies')}</Th>
                      <Th>{t('診断', 'Diagnoses')}</Th>
                      <Th>{t('最終ログイン', 'Last Login')}</Th>
                      <Th>{t('登録日', 'Created')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length === 0 ? (
                      <tr>
                        <Td colSpan={7} style={{ textAlign: 'center', padding: 32, color: '#8888a8' }}>
                          {t('ユーザーが見つかりません', 'No users found')}
                        </Td>
                      </tr>
                    ) : (
                      users.map((u) => (
                        <Tr key={u.id} onClick={() => navigate(`/admin/users/${u.id}`)}>
                          <Td style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.78rem', color: '#8888a8' }}>
                            {u.id}
                          </Td>
                          <Td>
                            <span style={{ fontWeight: 600 }}>{u.name || '--'}</span>
                            {u.is_admin && <AdminBadge>ADMIN</AdminBadge>}
                          </Td>
                          <Td style={{ fontSize: '0.82rem' }}>{u.email}</Td>
                          <Td style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>
                            {u.membership_count}
                          </Td>
                          <Td style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>
                            {u.diagnosis_count}
                          </Td>
                          <Td style={{ fontSize: '0.82rem', color: '#8888a8' }}>
                            {u.last_login ? new Date(u.last_login).toLocaleDateString() : '--'}
                          </Td>
                          <Td style={{ fontSize: '0.82rem', color: '#8888a8' }}>
                            {u.created_at ? new Date(u.created_at).toLocaleDateString() : '--'}
                          </Td>
                        </Tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </TableWrapper>
            </DesktopTable>

            <MobileCardList>
              {users.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 32, color: '#8888a8', fontSize: '0.85rem' }}>
                  {t('ユーザーが見つかりません', 'No users found')}
                </div>
              ) : (
                users.map((u) => (
                  <MobileCard key={u.id} onClick={() => navigate(`/admin/users/${u.id}`)}>
                    <MobileCardName>
                      {u.name || '--'}
                      {u.is_admin && <AdminBadge>ADMIN</AdminBadge>}
                    </MobileCardName>
                    <MobileCardEmail>{u.email}</MobileCardEmail>
                    <MobileCardStats>
                      <span>{t('企業', 'Co.')}: {u.membership_count}</span>
                      <span>{t('診断', 'Diag.')}: {u.diagnosis_count}</span>
                    </MobileCardStats>
                  </MobileCard>
                ))
              )}
            </MobileCardList>

            {pages > 1 && (
              <Pagination>
                <PageButton
                  disabled={page <= 1}
                  onClick={() => fetchUsers(page - 1, search)}
                >
                  {t('前へ', 'Prev')}
                </PageButton>
                {Array.from({ length: Math.min(pages, 7) }, (_, i) => {
                  const p = i + 1;
                  return (
                    <PageButton
                      key={p}
                      $active={p === page}
                      onClick={() => fetchUsers(p, search)}
                    >
                      {p}
                    </PageButton>
                  );
                })}
                <PageButton
                  disabled={page >= pages}
                  onClick={() => fetchUsers(page + 1, search)}
                >
                  {t('次へ', 'Next')}
                </PageButton>
              </Pagination>
            )}
          </>
        )}
      </Content>
    </AdminLayout>
  );
}

export default AdminUsersPage;
